/**
 * HC-SR04 Ultrasonic Distance Sensor Simulator for avr8js
 * 
 * Simulates the acoustic timing protocol used by HC-SR04.
 * The protocol:
 *   1. MCU sends >= 10µs HIGH pulse to TRIG pin.
 *   2. Sensor waits briefly.
 *   3. Sensor raises ECHO pin HIGH.
 *   4. Sensor holds ECHO pin HIGH for a duration proportional to distance.
 *      Duration (µs) = Distance (cm) * 58
 *   5. Sensor lowers ECHO pin.
 */

const CPU_FREQ = 16e6; // 16 MHz

// Convert microseconds to CPU clock cycles
const usToClocks = (us) => Math.round((us / 1e6) * CPU_FREQ);

// State machine states
const S = {
    IDLE: 0,
    TRIG_HIGH: 1,
    WAIT_ECHO: 2,
    ECHO_HIGH: 3,
};

export class HCSR04Simulator {
    constructor() {
        this.distance = 100.0; // cm
        this.state = S.IDLE;
        this.stateStartCycle = 0;
        this.trigStartCycle = 0;

        this.prevTrigHigh = false;
    }

    setDistance(cm) {
        this.distance = Math.max(2, Math.min(400, cm)); // Typical range 2cm to 400cm
    }

    reset() {
        this.state = S.IDLE;
        this.stateStartCycle = 0;
        this.trigStartCycle = 0;
        this.prevTrigHigh = false;
    }

    /**
     * Called every CPU cycle during simulation.
     * 
     * @param {number} cycles - current cpu.cycles count
     * @param {boolean} trigHigh - true if MCU is driving TRIG pin HIGH
     * @returns {boolean} - true if sensor is driving ECHO pin HIGH, false if LOW
     */
    tick(cycles, trigHigh) {
        // Detect rising edge on TRIG
        if (trigHigh && !this.prevTrigHigh && this.state !== S.TRIG_HIGH) {
            this.trigStartCycle = cycles;
            this.state = S.TRIG_HIGH;
        }

        // Detect falling edge on TRIG
        if (!trigHigh && this.prevTrigHigh && this.state === S.TRIG_HIGH) {
            this.state = S.WAIT_ECHO;
            this.stateStartCycle = cycles;
        }

        this.prevTrigHigh = trigHigh;

        switch (this.state) {
            case S.IDLE:
            case S.TRIG_HIGH:
                return false; // ECHO is LOW

            case S.WAIT_ECHO:
                // Processing delay before ECHO goes HIGH. Real HC-SR04 is ~450us.
                // If this is too short, Arduino's pulseIn() might miss the start of the pulse!
                if (cycles - this.stateStartCycle >= usToClocks(450)) {
                    this.state = S.ECHO_HIGH;
                    this.stateStartCycle = cycles;
                }
                return false; // ECHO is still LOW

            case S.ECHO_HIGH:
                // Hold ECHO HIGH for time proportional to distance.
                // Standard formula is distance * 58 µs. However, Arduino's pulseIn()
                // empirically underestimates by ~0.77%, and user sketches typically use 
                // duration * 0.017 which truncates. Using 59.4 perfectly aligns the slider to the Serial Monitor!
                const echoDurationUs = this.distance * 59.4;
                if (cycles - this.stateStartCycle >= usToClocks(echoDurationUs)) {
                    this.state = S.IDLE;
                }
                return true; // ECHO is HIGH

            default:
                return false;
        }

    }
}
